import React from 'react';

import type { EditorState } from 'prosemirror-state';

import { textColor } from '@atlaskit/adf-schema';
import type { ToolbarUIComponentFactory } from '@atlaskit/editor-common/types';

import type { CompanyHubTextColorPlugin } from '../../common/types/companyHubTextColorPlugin';
import { changeColor } from '../../controllers/commands/change-color';
import {
	type CompanyHubTextColorPluginConfig,
	pluginKey as companyHubTextColorPluginKey,
	createPlugin,
} from '../../controllers/company-hub-text-color-plugin';

import { PrimaryToolbarComponent } from './PrimaryToolbarComponent';

const pluginConfig = (
	textColorConfig?: CompanyHubTextColorPluginConfig | boolean,
): CompanyHubTextColorPluginConfig | undefined => {
	if (!textColorConfig || typeof textColorConfig === 'boolean') {
		return undefined;
	}

	return textColorConfig;
};

export const companyHubTextColorPlugin: CompanyHubTextColorPlugin = ({
	config: textColorConfig,
	api,
}) => {
	const primaryToolbarComponent: ToolbarUIComponentFactory = ({
		editorView,
		popupsMountPoint,
		popupsBoundariesElement,
		popupsScrollableElement,
		isToolbarReducedSpacing,
		dispatchAnalyticsEvent,
		disabled,
	}) => {
		if (!editorView) {
			return null;
		}

		return (
			<PrimaryToolbarComponent
				isReducedSpacing={isToolbarReducedSpacing}
				editorView={editorView}
				popupsMountPoint={popupsMountPoint}
				popupsBoundariesElement={popupsBoundariesElement}
				popupsScrollableElement={popupsScrollableElement}
				dispatchAnalyticsEvent={dispatchAnalyticsEvent}
				disabled={disabled}
				api={api}
			/>
		);
	};

	api?.primaryToolbar?.actions.registerComponent({
		name: 'textColor',
		component: primaryToolbarComponent,
	});

	return {
		name: 'companyHubTextColor',

		marks() {
			return [{ name: 'companyHubTextColor', mark: textColor }];
		},

		pmPlugins() {
			return [
				{
					name: 'companyHubTextColor',
					plugin: ({ dispatch }) => createPlugin(dispatch, pluginConfig(textColorConfig)),
				},
			];
		},

		getSharedState(editorState?: EditorState) {
			if (!editorState) {
				return undefined;
			}
			return companyHubTextColorPluginKey.getState(editorState);
		},

		actions: {
			changeColor: (color: string) => {
				return changeColor(color, api?.analytics?.actions);
			},
		},

		primaryToolbarComponent: !api?.primaryToolbar ? primaryToolbarComponent : undefined,
	};
};
